import { Injectable, signal } from '@angular/core';
import { DatabaseService } from '../../database/database_service';
import { ThemeConfigClass } from '../../classes/theme/theme-config-class';
import {
  updatePreset,
  updatePrimaryPalette,
  updateSurfacePalette,
} from '@primeng/themes';
import Lara from '@primeng/themes/lara';
import Aura from '@primeng/themes/aura';
import Material from '@primeng/themes/material';
import { ThemeConfigInterface } from '../../interfaces/theme/theme-config';
import Nora from '@primeng/themes/nora';
import { BroadcastChannelService } from '../../broadcast-channel/services/broadcast-channel.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  themeConfig: ThemeConfigInterface = new ThemeConfigClass();
  darkMode = signal(false);

  constructor(
    private dbService: DatabaseService,
    private broadcastChannelService: BroadcastChannelService
  ) {
    this.broadcastChannelService.themeChangeChannel.onmessage = (event) => {
      this.themeConfig = event.data;
      this.updatePreset();
    };

    /*    this.getThemeConfig();*/
  }

  async getThemeConfig() {
    await this.dbService
      .getDB('themeConfig')
      .then((f: ThemeConfigInterface) => {
        if (f) {
          this.themeConfig = f;
        }

        this.updatePreset();
      });
  }

  updateFontSize = () => {
    document.documentElement.style.setProperty(
      '--font-size',
      `${this.themeConfig.fontSize}px`
    );
  };

  updatePreset = () => {
    let baseTheme = Lara;
    if (this.themeConfig.baseTheme === 'Lara') {
      baseTheme = Lara;
    } else if (this.themeConfig.baseTheme === 'Aura') {
      baseTheme = Aura;
    } else if (this.themeConfig.baseTheme === 'Nora') {
      baseTheme = Nora;
    } else if (this.themeConfig.baseTheme === 'Material') {
      baseTheme = Material;
    }

    /*   charcoal: {
         50: '#f9f9fa',
           100: '#eaecee',
           200: '#d0d3d9',
           300: '#a7adb5',
           400: '#72777f',
           500: '#5a5e66',
           600: '#474b52',
           700: '#383b41',
           800: '#292b2e',
           900: '#18191b',
           950: '#0b0c0d',
       },*/

    updatePreset(baseTheme, {
      components: {
        chip: {
          root: {
            paddingX: '0',
            paddingY: '0',
          },
        },
        toggleswitch: {
          colorScheme: {
            dark: {
              root: {
                background: '{surface.600}',
              },
            },
          },
        },
        datatable: {
          header: {
            padding: '0.5rem',
          },
          headerCell: {
            padding: '0.5rem',
          },
          bodyCell: {
            padding: '0.5rem',
          },
          footerCell: {
            padding: '0.5rem',
          },
        },
      },
    });
    this.updateDarkMode();
    this.updatePrimary();
    this.updateSurface();
    this.updateFontSize();
  };

  updatePrimary = () => {
    updatePrimaryPalette({
      50: `{${this.themeConfig.primaryColor}.50}`,
      100: `{${this.themeConfig.primaryColor}.100}`,
      200: `{${this.themeConfig.primaryColor}.200}`,
      300: `{${this.themeConfig.primaryColor}.300}`,
      400: `{${this.themeConfig.primaryColor}.400}`,
      500: `{${this.themeConfig.primaryColor}.500}`,
      600: `{${this.themeConfig.primaryColor}.600}`,
      700: `{${this.themeConfig.primaryColor}.700}`,
      800: `{${this.themeConfig.primaryColor}.800}`,
      900: `{${this.themeConfig.primaryColor}.900}`,
      950: `{${this.themeConfig.primaryColor}.950}`,
    });
  };

  updateSurface = () => {
    updateSurfacePalette({
      light: {
        50: `{${this.themeConfig.surfaceLightColor}.50}`,
        100: `{${this.themeConfig.surfaceLightColor}.100}`,
        200: `{${this.themeConfig.surfaceLightColor}.200}`,
        300: `{${this.themeConfig.surfaceLightColor}.300}`,
        400: `{${this.themeConfig.surfaceLightColor}.400}`,
        500: `{${this.themeConfig.surfaceLightColor}.500}`,
        600: `{${this.themeConfig.surfaceLightColor}.600}`,
        700: `{${this.themeConfig.surfaceLightColor}.700}`,
        800: `{${this.themeConfig.surfaceLightColor}.800}`,
        900: `{${this.themeConfig.surfaceLightColor}.900}`,
        950: `{${this.themeConfig.surfaceLightColor}.950}`,
      },
      dark: {
        50: `{${this.themeConfig.surfaceDarkColor}.50}`,
        100: `{${this.themeConfig.surfaceDarkColor}.100}`,
        200: `{${this.themeConfig.surfaceDarkColor}.200}`,
        300: `{${this.themeConfig.surfaceDarkColor}.300}`,
        400: `{${this.themeConfig.surfaceDarkColor}.400}`,
        500: `{${this.themeConfig.surfaceDarkColor}.500}`,
        600: `{${this.themeConfig.surfaceDarkColor}.600}`,
        700: `{${this.themeConfig.surfaceDarkColor}.700}`,
        800: `{${this.themeConfig.surfaceDarkColor}.800}`,
        900: `{${this.themeConfig.surfaceDarkColor}.900}`,
        950: `{${this.themeConfig.surfaceDarkColor}.950}`,
      },
    });
  };

  updateDarkMode(): void {
    const root = document.documentElement;
    this.darkMode.set(this.themeConfig.darkMode);

    if (this.themeConfig.darkMode) {
      root.classList.add('dark');
      root.setAttribute('tuiTheme', 'night');
    } else {
      root.classList.remove('dark');
      root.setAttribute('tuiTheme', 'onLight');
    }
  }
}
